import React, { createContext, useState, useContext, ReactNode } from 'react';
import User from '../apiService/user/userApiService';
import { APIResponseType } from 'app/apiService/types';

// ----------- Type Definitions -----------
interface UserType {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  roles: string[];
  dealer_group_id: string;
  dealer_id?: string;
}

interface UserContextType {
  loading: boolean;
  error: string | null;
  success: boolean;
  user: UserType;
  setError: React.Dispatch<React.SetStateAction<string>>;
  setSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  setUser: React.Dispatch<React.SetStateAction<UserType>>;
  addUser: (user: UserType, callback?: () => void) => Promise<void>;
  deleteUser: (userId: string, callback?: () => void) => Promise<void>;
  editUser: (
    userId: string,
    data: Partial<UserType>,
    callback?: () => void,
  ) => Promise<void>;
  resetUserPassword: (userId: string) => Promise<APIResponseType>;
  getUsersByDealerGroupId: (dealerGroupId: string) => Promise<APIResponseType>;
}

// ----------- Context Create -----------
export const UserContext = createContext<UserContextType>({
  loading: false,
  error: null,
  success: false,
  setError: () => {},
  setSuccess: () => {},
  user: {
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    roles: [],
    dealer_group_id: '',
  },
  setUser: () => {},
  addUser: async () => {},
  deleteUser: async () => {},
  editUser: async () => {},
  resetUserPassword: async () => ({
    body: {},
    errorCode: undefined,
    message: '',
  }),
  getUsersByDealerGroupId: async () => ({
    body: {},
    errorCode: undefined,
    message: '',
  }),
});

// ----------- Provider Component -----------
export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [user, setUser] = useState<UserType>({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    roles: [],
    dealer_group_id: '',
  });

  // ----------- Success Handler -----------
  const handleSuccess = (callback?: () => void) => {
    setSuccess(true);
    setTimeout(() => {
      setSuccess(false);
      if (callback) callback();
    }, 2000); // Success message 2 sec ke liye
  };

  // ----------- API Calls -----------
  const addUser = async (user: UserType, callback?: () => void) => {
    setLoading(true);
    try {
      const response: APIResponseType = await User.addUser(user);
      if (response.errorCode) {
        setError(response.message || '');
      } else {
        setError('');
        handleSuccess(callback);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to add user');
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId: string, callback?: () => void) => {
    setLoading(true);
    setError('');
    try {
      await User.deleteUser(userId);
      handleSuccess(callback);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const editUser = async (
    userId: string,
    data: Partial<UserType>,
    callback?: () => void,
  ) => {
    setLoading(true);
    setError('');
    try {
      await User.editUser(userId, data);
      handleSuccess(callback);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to edit user');
    } finally {
      setLoading(false);
    }
  };

  const resetUserPassword = async (
    userId: string,
  ): Promise<APIResponseType> => {
    setLoading(true);
    setError('');
    try {
      const response = await User.resetUserPassword(userId);
      return response;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getUsersByDealerGroupId = async (
    dealerGroupId: string,
  ): Promise<APIResponseType> => {
    setLoading(true);
    setError('');
    try {
      const response = await User.getUsersByDealerGroupId(dealerGroupId);
      return response;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const contextValue: UserContextType = {
    loading,
    error,
    success,
    setError,
    setSuccess,
    user,
    setUser,
    addUser,
    deleteUser,
    editUser,
    resetUserPassword,
    getUsersByDealerGroupId,
  };

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
};

// ----------- Custom Hook -----------
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
